import {
  CP1255_HEBREW_FIRST_BYTE,
  CP1255_HEBREW_LAST_BYTE,
  ENCODING_DETECTION_CHUNK_SIZE,
  ENCODING_DETECTION_MAX_BYTES,
  HEBREW_LEGACY_ENCODING,
  MIN_FILE_NAME_PARTS_WITH_EXTENSION,
  UTF8_ENCODING,
} from "../constants";

export const getFileExtension = (fileName: string): string => {
  const nameParts = fileName.split(".");

  // A name with no dot has no extension - returning the whole name would let a file called
  // "csv" pass an extension check.
  if (nameParts.length < MIN_FILE_NAME_PARTS_WITH_EXTENSION) {
    return "";
  }

  return nameParts.pop()?.toLowerCase() ?? "";
};

const hasHebrewLegacyBytes = (bytes: Uint8Array): boolean => {
  return bytes.some(
    byte => byte >= CP1255_HEBREW_FIRST_BYTE && byte <= CP1255_HEBREW_LAST_BYTE,
  );
};

/**
 * Picks the encoding a text file should be read with, for parsers (papaparse, FileReader)
 * that always assume UTF-8.
 *
 * Only a bounded prefix is inspected, in chunks, and the scan stops at the first byte that
 * is not valid UTF-8 - so a Hebrew file is settled on its header row and a plain ASCII one
 * costs a single chunk. A file whose first ENCODING_DETECTION_MAX_BYTES are pure ASCII is
 * treated as UTF-8, which is also what the parsers would have assumed anyway.
 *
 * The fallback is claimed only when the file actually looks like legacy Hebrew: bytes in
 * the windows-1255 Hebrew range. Anything else invalid (another legacy encoding, a binary
 * file) stays on UTF-8 rather than being silently rewritten as Hebrew - a future encoding
 * gets its own check here.
 */
export const detectFileTextEncoding = async (file: File): Promise<string> => {
  // Fatal mode throws on an invalid sequence; stream mode keeps a multi-byte character
  // split across a chunk boundary from being mistaken for one.
  const decoder = new TextDecoder(UTF8_ENCODING, { fatal: true });
  const maxBytes = Math.min(file.size, ENCODING_DETECTION_MAX_BYTES);
  let hasHebrewBytes = false;

  for (
    let offset = 0;
    offset < maxBytes;
    offset += ENCODING_DETECTION_CHUNK_SIZE
  ) {
    const chunk = file.slice(offset, offset + ENCODING_DETECTION_CHUNK_SIZE);
    const bytes = new Uint8Array(await chunk.arrayBuffer());

    hasHebrewBytes = hasHebrewBytes || hasHebrewLegacyBytes(bytes);

    try {
      decoder.decode(bytes, { stream: true });
    } catch {
      return hasHebrewBytes ? HEBREW_LEGACY_ENCODING : UTF8_ENCODING;
    }
  }

  return UTF8_ENCODING;
};
